import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { protocolLabel, displayUnit } from "helpers/estimatingDatabase/displayFormats";
import addNotification from "helpers/notify";
import { formats } from "constants/utils";
import { CATALOG_MAP_ENDPOINTS, CATALOG_MAP_PAGE } from "constants/estimatingDatabasePages";
import { useDatabaseWorkspace, useScopedList } from "hooks/useEstimatingDatabase";
import { DatabasePageShell } from "components/EstimatingDatabase/DatabasePageShell";
import { useListPanel } from "components/EstimatingDatabase/useListPanel";
import { matchesSearch, SCOPE_BIDLIGHT } from "helpers/estimatingDatabase/databaseRows";
import {
  assignCodes,
  unassignCode,
  withDivisionAwp,
  toArray,
  divisionTickKeys,
  resourcesUnderDivisions,
} from "helpers/estimatingDatabase/catalogMapping";
import { useLookup, awpDisplay, catalogDivisionDisplay } from "components/EstimatingDatabase/lookups";
import { CREW_CONFIG, LABOR_CONFIG } from "./LaborDatabasePage";
import { TOOL_CONFIG, TOOL_GROUP_CONFIG } from "./ToolsDatabasePage";

const RENDER_LIMIT = 400;

const catalogConfig = (endpoint) => ({
  endpoint,
  fields: [{ key: "CrewCodes" }, { key: "EquipmentCodes" }],
  fromWire: (w) => ({ ...w, CrewCodes: toArray(w.CrewCodes), EquipmentCodes: toArray(w.EquipmentCodes) }),
  // Only the mapping is written; the server $sets just these fields.
  toWire: (rec) => ({ Code: rec.Code, CrewCodes: toArray(rec.CrewCodes), EquipmentCodes: toArray(rec.EquipmentCodes) }),
});

export default function CatalogMappingPage() {
  const workspace = useDatabaseWorkspace();
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("mapping");
  const [activeKey, setActiveKey] = useState(null);
  const [crewSearch, setCrewSearch] = useState("");
  const [toolSearch, setToolSearch] = useState("");

  const protocol = (workspace.template && workspace.template.Protocol) || "RevitUniFormat";
  const catalogCfg = useMemo(() => catalogConfig(formats[protocol] || "catalogs"), [protocol]);
  const mapCfg = useMemo(
    () => ({ ...CATALOG_MAP_PAGE, endpoint: CATALOG_MAP_ENDPOINTS[protocol] || "catalog_maps" }),
    [protocol]
  );

  const catalogs = useScopedList(workspace, catalogCfg);
  const crews = useScopedList(workspace, CREW_CONFIG);
  const labors = useScopedList(workspace, LABOR_CONFIG);
  const groups = useScopedList(workspace, TOOL_GROUP_CONFIG);
  const tools = useScopedList(workspace, TOOL_CONFIG);
  const familyMaps = useScopedList(workspace, mapCfg);
  const bidlight = workspace.scope === SCOPE_BIDLIGHT;

  // AwpUtil.DbItems: the division's AWP combo shows "<Awp> <Description>".
  const awps = useLookup(workspace, "awps");

  // A model mapping's Division is a catalog division code of this version.
  const mapPanel = useListPanel({
    list: familyMaps,
    config: mapCfg,
    workspace,
    search,
    optionLabel: (field, v) => {
      if (v === undefined || v === null || v === "") return "--";
      if (field.key === "Division") return catalogDivisionDisplay(catalogs.rawRows, v);
      return String(v);
    },
  });

  const sortedCatalogs = useMemo(
    () =>
      [...catalogs.items].sort((a, b) =>
        String(a.rec.Code).localeCompare(String(b.rec.Code), undefined, { numeric: true })
      ),
    [catalogs.items]
  );
  const visibleCatalogs = sortedCatalogs.filter((it) => matchesSearch(it.rec, ["Code", "Description"], search));
  const active = catalogs.items.find((it) => it.key === activeKey) || null;

  const crewByCode = useMemo(() => new Map(crews.items.map((c) => [String(c.rec.Code), c])), [crews.items]);
  const toolByCode = useMemo(() => new Map(tools.items.map((t) => [String(t.rec.Code), t])), [tools.items]);

  const guardEdit = () => {
    if (bidlight) {
      addNotification(
        "Mapping can't be changed in the BidLight database. Switch to the company database to modify the mappings.",
        "danger"
      );
      return false;
    }
    if (!workspace.canEdit) {
      addNotification("You need the pricing database edit permission to change mappings.", "danger");
      return false;
    }
    if (!active) {
      addNotification("Select a catalog division first.", "danger");
      return false;
    }
    return true;
  };

  const addAwp = (list, item) => {
    const next = withDivisionAwp(item.rec, active.rec.Awp);
    if (next) list.edit(item.key, { Awp: next.Awp });
  };

  // `target` is the division a crew or tool was dropped on; the buttons use
  // the selected one.
  const assignCrew = (crew, target = active) => {
    if (!guardEdit() || !target) return;
    catalogs.edit(target.key, assignCodes(target.rec, "CrewCodes", [String(crew.rec.Code)]));
    addAwp(crews, crew);
  };

  const assignTools = (toolItems, target = active) => {
    if (!guardEdit() || !toolItems.length || !target) return;
    catalogs.edit(target.key, assignCodes(target.rec, "EquipmentCodes", toolItems.map((t) => String(t.rec.Code))));
    toolItems.forEach((t) => addAwp(tools, t));
  };

  // DRAG AND DROP (CatalogMappingPage.xaml): a crew, a tool, a tool type or a
  // whole category dragged from the right onto a division assigns it there.
  const DRAG_TYPE = "application/x-bidlight-mapping";
  const dragStart = (payload) => (e) => {
    e.dataTransfer.setData(DRAG_TYPE, JSON.stringify(payload));
    e.dataTransfer.effectAllowed = "copy";
  };
  const [dropKey, setDropKey] = useState("");
  const dropOn = (it) => ({
    onDragOver: (e) => {
      if (!Array.from(e.dataTransfer.types || []).includes(DRAG_TYPE)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
      if (dropKey !== it.key) setDropKey(it.key);
    },
    onDragLeave: () => setDropKey((k) => (k === it.key ? "" : k)),
    onDrop: (e) => {
      e.preventDefault();
      setDropKey("");
      let data = null;
      try { data = JSON.parse(e.dataTransfer.getData(DRAG_TYPE)); } catch (err) { data = null; }
      if (!data) return;
      setActiveKey(it.key);
      if (data.kind === "crew") {
        const crew = crewByCode.get(String(data.code));
        if (crew) assignCrew(crew, it);
      } else if (data.kind === "tools") {
        assignTools((data.codes || []).map((c) => toolByCode.get(String(c))).filter(Boolean), it);
      }
    },
  });

  // Category column: the catalog's Revit category keys by name, when the
  // categories are loaded (the Catalog tab loads them).
  const allCategories = useSelector((s) => s.pricing && s.pricing.allCategories);
  const categoryName = useMemo(() => {
    const m = new Map();
    (allCategories || []).forEach((c) => {
      const r = (c && (c.company || c.default || c)) || {};
      if (r.Key !== undefined) m.set(String(r.Key), r.Name || r.Description || String(r.Key));
    });
    // a key with no loaded name is left out: the raw key is a 64-character hash
    return (key) => m.get(String(key)) || "";
  }, [allCategories]);

  const unassign = (field, code) => {
    if (bidlight || !workspace.canEdit || !active) return;
    catalogs.edit(active.key, unassignCode(active.rec, field, code));
  };

  const toolTree = useMemo(() => {
    const byGroup = new Map();
    tools.items.forEach((t) => {
      const g = String(t.rec.Group || "");
      if (!byGroup.has(g)) byGroup.set(g, []);
      byGroup.get(g).push(t);
    });
    const cats = new Map();
    groups.items.forEach((g) => {
      const c = g.rec.Category || "";
      if (!cats.has(c)) cats.set(c, []);
      cats.get(c).push({ group: g, tools: byGroup.get(String(g.rec.Code)) || [] });
    });
    return [...cats.entries()].map(([category, gs]) => ({ category, groups: gs }));
  }, [groups.items, tools.items]);

  // CatalogMappingDatabaseViewModel.SaveToApi / SaveToOtherVersionAPI /
  // SaveToDefaultDataAPI: the ticked divisions are saved, and the crews
  // (with all their labours) and tools (with their types) mapped on them
  // are posted with Override. The count that gates saving is the
  // divisions' (GetDataChangedCount).
  const tickedDivisions = catalogs.items.filter((it) => it.selected).map((it) => it.rec);
  const mappedResources = () =>
    resourcesUnderDivisions(tickedDivisions, {
      crews: crews.items.map((c) => c.rec),
      labors: labors.items.map((l) => l.rec),
      tools: tools.items.map((t) => t.rec),
      groups: groups.items.map((g) => g.rec),
    });
  const postResources = async (target) => {
    const r = mappedResources();
    const results = [
      await crews.copyTo({ ...target, records: r.crews }),
      await labors.copyTo({ ...target, records: r.labors }),
      await groups.copyTo({ ...target, records: r.groups }),
      await tools.copyTo({ ...target, records: r.tools }),
    ];
    return {
      errors: results.flatMap((x) => x.errors || []),
      count: results.reduce((s2, x) => s2 + (x.count || 0), 0),
    };
  };
  const mappingResources = {
    pending: 0,
    copyChanges: 0,
    loading: crews.loading || labors.loading || groups.loading || tools.loading,
    reload: async () => {
      await crews.reload();
      await labors.reload();
      await groups.reload();
      await tools.reload();
    },
    save: () => postResources({}),
    copyTo: (target) => postResources(target),
  };
  const lists = bidlight ? [] : [catalogs, mappingResources, familyMaps];
  const mapping = tab === "mapping";

  return (
    <DatabasePageShell
      title="Catalog Mapping"
      workspace={workspace}
      lists={lists}
      searchValue={search}
      setSearchValue={setSearch}
      onAdd={mapping ? () => addNotification("Pick a crew or tool on the right and assign it to the selected division.", "danger") : mapPanel.onAdd}
      onEdit={mapping ? () => addNotification("Select a division and assign or remove crews and tools.", "danger") : mapPanel.onEdit}
      onDelete={mapping ? () => addNotification("Use the remove buttons next to the mapped crews and tools.", "danger") : mapPanel.onDelete}
      onClone={mapping ? undefined : mapPanel.onClone}
      onExport={mapPanel.onExport}
      onImport={mapPanel.onImport}
      disableAdd={mapping}
    >
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button type="button" className={`nav-link btn btn-link ${mapping ? "active" : ""}`} onClick={() => setTab("mapping")}>
            Crews and tools per division
          </button>
        </li>
        <li className="nav-item">
          <button type="button" className={`nav-link btn btn-link ${!mapping ? "active" : ""}`} onClick={() => setTab("models")}>
            Model mappings ({protocolLabel(protocol)})
          </button>
        </li>
      </ul>

      {!mapping && mapPanel.view}

      {mapping && (
        <div className="d-flex gap-3 flex-wrap" data-testid="catalog-mapping">
          <div style={{ flex: "1 1 360px", minWidth: 300 }}>
            <div className="f-14 font-weight-bold mb-1">Catalog ({protocolLabel(protocol)})</div>
            <div style={{ maxHeight: "calc(100 * var(--vh) - 380px)", overflow: "auto", border: "1px solid #E2E2E2", borderRadius: 6 }}>
              {visibleCatalogs.slice(0, RENDER_LIMIT).map((it) => {
                const crewCount = toArray(it.rec.CrewCodes).length;
                const toolCount = toArray(it.rec.EquipmentCodes).length;
                return (
                  <div
                    key={it.key}
                    data-testid="catalog-division"
                    className={`px-2 py-1 f-14 db-division ${it.key === activeKey ? "table-active" : ""}${dropKey === it.key ? " db-drop-target" : ""}`}
                    style={{ cursor: "pointer", paddingLeft: 8 + ((Number(it.rec.Level) || 1) - 1) * 14 }}
                    onClick={() => setActiveKey(it.key)}
                    {...(bidlight ? {} : dropOn(it))}
                  >
                    {!bidlight && (
                      <input
                        type="checkbox"
                        className="squared-checkbox mr-2"
                        aria-label="Select division"
                        checked={!!it.selected}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => catalogs.select(divisionTickKeys(sortedCatalogs, it.key, e.target.checked), e.target.checked)}
                      />
                    )}
                    <strong>{it.rec.Code}</strong> {it.rec.Description}
                    {/* the desktop's count badges, then Unit / Category / AWP (read-only) */}
                    <span className="db-count db-count-crews" title="Crews" data-testid="crew-count">{crewCount}</span>
                    <span className="db-count db-count-tools" title="Tools" data-testid="tool-count">{toolCount}</span>
                    <span className="db-division-meta">
                      {it.rec.Unit ? <span title="Unit">{displayUnit(it.rec.Unit)}</span> : null}
                      {toArray(it.rec.CategoryId).map(categoryName).filter(Boolean).length ? (
                        <span title="Category">{toArray(it.rec.CategoryId).map(categoryName).filter(Boolean).join(", ")}</span>
                      ) : null}
                      {it.rec.Awp ? <span title="AWP">{awpDisplay(awps, it.rec.Awp)}</span> : null}
                    </span>
                    {it.status === "dirty" && <span className="text-muted f-12"> - edited</span>}
                  </div>
                );
              })}
              {visibleCatalogs.length > RENDER_LIMIT && (
                <div className="px-2 py-1 f-12 text-muted">
                  Showing {RENDER_LIMIT} of {visibleCatalogs.length}; search to narrow the list.
                </div>
              )}
              {!visibleCatalogs.length && <div className="px-2 py-3 f-14 text-muted">No catalog divisions.</div>}
            </div>
          </div>

          <div style={{ flex: "1 1 300px", minWidth: 260 }}>
            <div className="f-14 font-weight-bold mb-1">
              {active ? `${active.rec.Code} ${active.rec.Description || ""}` : "Select a division"}
            </div>
            {active && (
              <>
                <div className="f-12 text-muted mb-2" data-testid="division-awp">AWP: {awpDisplay(awps, active.rec.Awp) || "--"}</div>
                <div className="f-14 font-weight-bold">Crews</div>
                {toArray(active.rec.CrewCodes).map((code) => {
                  const c = crewByCode.get(code);
                  return (
                    <div key={code} className="d-flex justify-content-between align-items-center f-14 py-1" data-testid="mapped-crew">
                      <span>
                        {code} {c ? c.rec.Description : "(not in this version)"}
                      </span>
                      {!bidlight && (
                        <button type="button" className="btn btn-link btn-sm p-0" onClick={() => unassign("CrewCodes", code)}>
                          remove
                        </button>
                      )}
                    </div>
                  );
                })}
                {!toArray(active.rec.CrewCodes).length && <div className="f-12 text-muted">None</div>}
                <div className="f-14 font-weight-bold mt-3">Tools</div>
                {toArray(active.rec.EquipmentCodes).map((code) => {
                  const t = toolByCode.get(code);
                  return (
                    <div key={code} className="d-flex justify-content-between align-items-center f-14 py-1" data-testid="mapped-tool">
                      <span>
                        {code} {t ? t.rec.Description : "(not in this version)"}
                      </span>
                      {!bidlight && (
                        <button type="button" className="btn btn-link btn-sm p-0" onClick={() => unassign("EquipmentCodes", code)}>
                          remove
                        </button>
                      )}
                    </div>
                  );
                })}
                {!toArray(active.rec.EquipmentCodes).length && <div className="f-12 text-muted">None</div>}
              </>
            )}
          </div>

          <div style={{ flex: "1 1 320px", minWidth: 280 }}>
            <div className="f-14 font-weight-bold mb-1">Crews</div>
            <input
              className="form-control form-control-sm mb-1"
              placeholder="Search crews"
              value={crewSearch}
              onChange={(e) => setCrewSearch(e.target.value)}
            />
            <div style={{ maxHeight: 220, overflow: "auto", border: "1px solid #E2E2E2", borderRadius: 6 }}>
              {crews.items
                .filter((c) => matchesSearch(c.rec, ["Code", "Description"], crewSearch))
                .map((c) => (
                  <div
                    key={c.key}
                    className="d-flex justify-content-between align-items-center px-2 py-1 f-14 db-draggable"
                    draggable={!bidlight}
                    onDragStart={dragStart({ kind: "crew", code: String(c.rec.Code) })}
                    title="Drag onto a division to assign"
                  >
                    <span>
                      {c.rec.Code} {c.rec.Description}
                    </span>
                    <button type="button" className="btn btn-link btn-sm p-0" onClick={() => assignCrew(c)} data-testid="assign-crew">
                      assign
                    </button>
                  </div>
                ))}
            </div>
            <div className="f-14 font-weight-bold mt-3 mb-1">Tools</div>
            <input
              className="form-control form-control-sm mb-1"
              placeholder="Search tools"
              value={toolSearch}
              onChange={(e) => setToolSearch(e.target.value)}
            />
            <div style={{ maxHeight: 260, overflow: "auto", border: "1px solid #E2E2E2", borderRadius: 6 }}>
              {toolTree.map(({ category, groups: gs }) => (
                <div key={category || "-"}>
                  <div
                    className="d-flex justify-content-between align-items-center px-2 py-1 f-14 font-weight-bold db-draggable"
                    draggable={!bidlight}
                    onDragStart={dragStart({ kind: "tools", codes: gs.flatMap((g) => g.tools).map((t) => String(t.rec.Code)) })}
                  >
                    <span>{category || "(no category)"}</span>
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0"
                      onClick={() => assignTools(gs.flatMap((g) => g.tools))}
                    >
                      assign all
                    </button>
                  </div>
                  {gs.map(({ group, tools: ts }) => (
                    <div key={group.key}>
                      <div
                        className="d-flex justify-content-between align-items-center px-3 py-1 f-14 db-draggable"
                        draggable={!bidlight}
                        onDragStart={dragStart({ kind: "tools", codes: ts.map((t) => String(t.rec.Code)) })}
                      >
                        <span>
                          {group.rec.Code} {group.rec.Type}
                        </span>
                        <button type="button" className="btn btn-link btn-sm p-0" onClick={() => assignTools(ts)}>
                          assign type
                        </button>
                      </div>
                      {ts
                        .filter((t) => matchesSearch(t.rec, ["Code", "Description", "Model"], toolSearch))
                        .map((t) => (
                          <div
                            key={t.key}
                            className="d-flex justify-content-between align-items-center px-4 py-1 f-14 db-draggable"
                            draggable={!bidlight}
                            onDragStart={dragStart({ kind: "tools", codes: [String(t.rec.Code)] })}
                          >
                            <span>
                              {t.rec.Code} {t.rec.Description}
                            </span>
                            <button
                              type="button"
                              className="btn btn-link btn-sm p-0"
                              onClick={() => assignTools([t])}
                              data-testid="assign-tool"
                            >
                              assign
                            </button>
                          </div>
                        ))}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </DatabasePageShell>
  );
}
